import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerPortalAPI, warrantiesAPI, liveChatAPI } from '../api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { toast } from 'sonner';
import {
  TicketIcon, ShieldCheck, Calendar, MessageSquare, FileImage, Star,
  ArrowRight, RefreshCw, Inbox, Loader2, CheckCircle, ExternalLink
} from 'lucide-react';

const ITEMS = [
  { key: 'open_tickets', recent: 'recent_tickets', label: 'Open Tickets', icon: TicketIcon, color: 'bg-red-100 text-red-700', path: '/service-hub', action: 'view' },
  { key: 'pending_warranties', recent: 'recent_warranties', label: 'Warranty Registrations', icon: ShieldCheck, color: 'bg-amber-100 text-amber-700', path: '/warranties', action: 'approve' },
  { key: 'pending_bookings', recent: 'recent_bookings', label: 'Meeting Requests', icon: Calendar, color: 'bg-blue-100 text-blue-700', path: '/calendar', action: 'view' },
  { key: 'waiting_chats', recent: 'recent_chats', label: 'Waiting Chats', icon: MessageSquare, color: 'bg-purple-100 text-purple-700', path: '/live-chat/dashboard', action: 'claim' },
  { key: 'pending_files', recent: 'recent_files', label: 'File Uploads', icon: FileImage, color: 'bg-teal-100 text-teal-700', path: '/customer-portal-admin', action: 'view' },
  { key: 'pending_testimonials', recent: 'recent_testimonials', label: 'Testimonials', icon: Star, color: 'bg-emerald-100 text-emerald-700', path: '/customer-portal-admin', action: 'view' },
];

const itemLabel = (cat, it) => {
  if (cat.key === 'open_tickets') return `${it.ticket_number || ''} ${it.subject || ''}`.trim() || 'Ticket';
  if (cat.key === 'pending_warranties') return `${it.product_name || 'Product'} — SN ${it.serial_number || 'N/A'}`;
  if (cat.key === 'pending_bookings') return `${it.customer_name || 'Customer'}`;
  if (cat.key === 'waiting_chats') return it.visitor_name || 'Visitor';
  if (cat.key === 'pending_files') return `${it.title || 'File'} (${it.contact_name || ''})`;
  if (cat.key === 'pending_testimonials') return `${it.title || 'Testimonial'} (${it.contact_name || ''})`;
  return it.title || it.subject || 'Item';
};

export const CustomerPortalActivityPanel = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openCat, setOpenCat] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const fetchCounts = async () => {
    try {
      const res = await customerPortalAPI.adminGetPendingCounts();
      setData(res.data);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  if (error || (!loading && !data)) return null;

  const approveWarranty = async (id) => {
    setBusyId(id);
    try {
      await warrantiesAPI.update(id, { status: 'active' });
      toast.success('Warranty approved & activated');
      await fetchCounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const claimChat = async (id) => {
    setBusyId(id);
    try {
      await liveChatAPI.assignChat(id);
      toast.success('Chat claimed');
      navigate('/live-chat/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to claim chat');
    } finally {
      setBusyId(null);
    }
  };

  const total = data?.total_pending || 0;
  const activeCat = ITEMS.find(i => i.key === openCat);
  const recentItems = activeCat ? (data?.[activeCat.recent] || []) : [];

  return (
    <>
      <Card className="border-blue-200" data-testid="portal-activity-panel">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Inbox className="h-5 w-5 text-blue-600" />
            Customer Portal Activity
            {total > 0 && <Badge className="bg-blue-600 text-white">{total} need attention</Badge>}
          </CardTitle>
          <button onClick={fetchCounts} className="text-slate-400 hover:text-slate-600" title="Refresh" data-testid="portal-activity-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </CardHeader>
        <CardContent>
          {total === 0 && !loading ? (
            <p className="text-sm text-muted-foreground py-2">All caught up — no pending customer submissions.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {ITEMS.map(({ key, label, icon: Icon, color }) => {
                const count = data?.[key] || 0;
                return (
                  <button
                    key={key}
                    onClick={() => setOpenCat(key)}
                    className={`group text-left p-3 rounded-xl border transition-all hover:shadow-md ${count > 0 ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50'}`}
                    data-testid={`portal-activity-${key}`}
                  >
                    <div className={`inline-flex p-2 rounded-lg mb-2 ${color}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className={`text-2xl font-bold ${count > 0 ? 'text-slate-900' : 'text-slate-400'}`}>{count}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      {label}
                      <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick-action dialog per category */}
      <Dialog open={!!openCat} onOpenChange={(o) => { if (!o) setOpenCat(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activeCat && <activeCat.icon className="h-5 w-5 text-blue-600" />}
              {activeCat?.label}
            </DialogTitle>
            <DialogDescription>
              {recentItems.length > 0 ? 'Most recent items needing attention.' : 'Nothing pending in this category.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {recentItems.map((it, idx) => (
              <div key={it.id || idx} className="flex items-center justify-between gap-2 p-2 border rounded-lg" data-testid={`activity-item-${it.id || idx}`}>
                <span className="text-sm truncate">{itemLabel(activeCat, it)}</span>
                {activeCat?.action === 'approve' ? (
                  <Button size="sm" disabled={busyId === it.id} onClick={() => approveWarranty(it.id)} data-testid={`approve-${it.id}`}>
                    {busyId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-1" /> Approve</>}
                  </Button>
                ) : activeCat?.action === 'claim' ? (
                  <Button size="sm" disabled={busyId === it.id} onClick={() => claimChat(it.id)} data-testid={`claim-${it.id}`}>
                    {busyId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Claim & Open'}
                  </Button>
                ) : null}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { const p = activeCat?.path; setOpenCat(null); if (p) navigate(p); }} data-testid="activity-open-page">
              <ExternalLink className="h-4 w-4 mr-1" /> Open {activeCat?.label} page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerPortalActivityPanel;
